import React, { useState, useRef, useEffect } from 'react';
import { Store, Plus, X, ChevronDown, Check } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BranchSelector — Selector de "Sucursal Activa" para clientes B2B.
 * - Visible sólo si `customer_type === 'comercio'`.
 * - Se ve como un chip pill en el header del catálogo.
 * - Permite: elegir de la lista guardada del user, agregar nueva, o dejar
 *   sin sucursal ("Sin asignar").
 * - Al agregar una sucursal nueva se persiste vía PUT /api/auth/branches.
 * - Los productos que el usuario agregue AL CATÁLOGO se etiquetan con esta
 *   sucursal automáticamente. Cambiar de sucursal NO borra el carrito —
 *   los items ya agregados mantienen su etiqueta original.
 */
const BranchSelector = ({ compact = false }) => {
  const { activeBranch, setActiveBranch } = useCart();
  const { user, refreshUser, getEffectiveCustomerType } = useAuth();
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  // Anchoring dinámico del dropdown: si el trigger queda cerca del borde
  // izquierdo del viewport (ej. logoAlign='right') el menú anclado a la
  // derecha se sale de pantalla. Elegimos left/right según el rect real.
  const [menuAlign, setMenuAlign] = useState('right');
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // 🆕 Si el usuario abre el selector y NO tiene sucursales guardadas, auto-
  // activamos el modo "agregar" para que el foco caiga directo en el input y
  // el flujo sea "abrir → escribir nombre → guardar", sin pasos intermedios.
  useEffect(() => {
    if (!open) return;
    if (!Array.isArray(user?.branches) || user.branches.length === 0) {
      setAdding(true);
    }
  }, [open, user?.branches]);

  // Al abrir, medir posición del trigger y decidir a qué lado abrir el menú.
  useEffect(() => {
    if (!open || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const MENU_W = 288; // w-72
    // Si al anclar right-0 el menú se sale por la izquierda (rect.right < MENU_W)
    // usamos left-0. Si al anclar left-0 se sale por la derecha (rect.left + MENU_W > vw)
    // mantenemos right-0.
    if (rect.right < MENU_W + 8) setMenuAlign('left');
    else if (rect.left + MENU_W > vw - 8) setMenuAlign('right');
    else setMenuAlign('right');
  }, [open]);

  // B2B (comercio) + Admin ven el selector. Admin lo usa para preview/QA.
  const effType = getEffectiveCustomerType ? getEffectiveCustomerType() : user?.customer_type;
  const isAdmin = user?.role === 'admin';
  if (effType !== 'comercio' && !isAdmin) return null;

  const branches = Array.isArray(user?.branches) ? user.branches : [];

  const saveBranches = async (nextList) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/auth/branches`, { branches: nextList }, { withCredentials: true });
      if (refreshUser) await refreshUser();
      return r.data.branches;
    } catch (e) {
      // Feedback visible al usuario (antes se comía el error en consola).
      const msg = e?.response?.data?.detail || 'No pudimos guardar tus sucursales. Reintentá.';
      try { window.alert(msg); } catch { /* ignore */ }
      return null;
    } finally {
      setSaving(false);
    }
  };

  const addBranch = async () => {
    const name = (newName || '').trim();
    if (!name) return;
    const already = branches.some(b => b.toLowerCase() === name.toLowerCase());
    let next = branches;
    if (!already) {
      next = [...branches, name];
      const saved = await saveBranches(next);
      if (saved) next = saved;
    }
    setActiveBranch(name);
    setNewName('');
    setAdding(false);
    setOpen(false);
  };

  const removeBranch = async (name) => {
    if (!window.confirm(`¿Eliminar "${name}" de tus sucursales?`)) return;
    const next = branches.filter(b => b.toLowerCase() !== name.toLowerCase());
    await saveBranches(next);
    if (activeBranch && activeBranch.toLowerCase() === name.toLowerCase()) {
      setActiveBranch('');
    }
  };

  return (
    <div ref={ref} className="relative" data-testid="branch-selector-root">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`inline-flex items-center gap-2 rounded-full border-2 transition-all shadow-sm min-w-0 max-w-full ${
          activeBranch
            ? 'bg-orange-50 border-orange-400 text-orange-800'
            : 'bg-white border-slate-200 text-slate-600 hover:border-orange-300'
        } ${compact ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm'}`}
        data-testid="branch-selector-trigger"
        title={activeBranch || 'Sucursal: sin asignar'}
      >
        <Store size={compact ? 14 : 16} className="flex-shrink-0" />
        {/* Label oculta en mobile (<640px) para que el pill entre siempre en
            su celda del grid del header y no se solape con el logo. En sm+
            se muestra con truncate. */}
        <span className="font-bold hidden sm:inline sm:max-w-[140px] truncate min-w-0" data-testid="branch-selector-active">
          {activeBranch || 'Sucursal: sin asignar'}
        </span>
        <ChevronDown size={compact ? 12 : 14} className={`transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className={`absolute ${menuAlign === 'left' ? 'left-0' : 'right-0'} mt-2 w-72 max-w-[calc(100vw-1rem)] bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50`} data-testid="branch-selector-menu">
          <div className="px-3 py-2 bg-gradient-to-r from-orange-50 to-amber-50 border-b border-slate-200">
            <p className="text-[11px] font-bold uppercase text-orange-700 tracking-wider">Sucursal activa</p>
            <p className="text-[10px] text-slate-600 mt-0.5 leading-tight">
              Los productos que agregues al carrito se etiquetan con esta sucursal.
            </p>
          </div>
          <button
            type="button"
            onClick={() => { setActiveBranch(''); setOpen(false); }}
            className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center justify-between border-b border-slate-100 ${!activeBranch ? 'bg-orange-50' : ''}`}
            data-testid="branch-selector-none"
          >
            <span className="text-slate-700">Sin asignar</span>
            {!activeBranch && <Check size={14} className="text-orange-600" />}
          </button>
          <div className="max-h-64 overflow-y-auto">
            {branches.length === 0 && !adding && (
              <div className="px-3 py-4 text-xs text-slate-400 text-center">
                Todavía no cargaste sucursales.
              </div>
            )}
            {branches.map((b, idx) => (
              <div
                key={idx}
                className={`flex items-center justify-between px-3 py-2 border-b border-slate-100 hover:bg-slate-50 ${
                  activeBranch && activeBranch.toLowerCase() === b.toLowerCase() ? 'bg-orange-50' : ''
                }`}
                data-testid={`branch-selector-item-${idx}`}
              >
                <button
                  type="button"
                  onClick={() => { setActiveBranch(b); setOpen(false); }}
                  className="flex-1 text-left text-sm text-slate-800 truncate flex items-center gap-2"
                >
                  <span className="truncate">{b}</span>
                  {activeBranch && activeBranch.toLowerCase() === b.toLowerCase() && (
                    <Check size={14} className="text-orange-600 flex-shrink-0" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => removeBranch(b)}
                  className="text-slate-400 hover:text-red-500 ml-2 p-1 rounded"
                  title="Eliminar sucursal"
                  aria-label={`Eliminar ${b}`}
                  data-testid={`branch-selector-delete-${idx}`}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
          {adding ? (
            <div className="p-2 border-t border-slate-200 bg-slate-50 space-y-2">
              <input
                type="text"
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') addBranch(); }}
                placeholder="Ej: Kiosco Ori 1"
                maxLength={60}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
                data-testid="branch-selector-new-name"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setAdding(false); setNewName(''); }}
                  className="flex-1 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600 border border-slate-300 hover:bg-slate-100"
                  data-testid="branch-selector-cancel-btn"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={addBranch}
                  disabled={!newName.trim() || saving}
                  className="flex-1 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-60"
                  data-testid="branch-selector-save-btn"
                >
                  {saving ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-bold text-white shadow-inner"
              style={{ backgroundColor: '#EA580C' }}
              data-testid="branch-selector-add-btn"
            >
              <Plus size={16} /> Agregar nueva sucursal
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default BranchSelector;
